import { type FormEvent, useEffect, useState } from 'react';
import { api, errorMessage } from '../../api';
import { useResource } from '../../hooks';
import { resetClientConfig } from '../../maps';

/**
 * The Gmail account the factures leave from. Google refuses a plain
 * password: it takes a « mot de passe d'application », generated for this app alone
 * and revocable from the Google account. It is stored on the server and never sent
 * back — the field stays empty and only says whether one is recorded, so saving the
 * rest of the panel must not touch it.
 */
export function Email() {
  const settings = useResource(() => api.settings.get(), []);
  const [user, setUser] = useState('');
  const [fromName, setFromName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!settings.data) return;
    setUser(settings.data.smtpUser);
    setFromName(settings.data.smtpFromName);
    setSubject(settings.data.invoiceEmailSubject);
    setBody(settings.data.invoiceEmailBody);
    setPassword('');
  }, [settings.data]);

  const configured = settings.data?.smtpConfigured ?? false;
  const managedByEnv = settings.data?.smtpManagedByEnv ?? false;

  async function run(action: () => Promise<string>) {
    setPending(true);
    setError(null);
    setNotice(null);
    try {
      setNotice(await action());
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const address = user.trim();
    await run(async () => {
      await api.settings.update({
        smtpUser: address || null,
        smtpFromName: fromName,
        invoiceEmailSubject: subject,
        invoiceEmailBody: body,
        // An untouched field keeps the stored password; sending '' would clear it.
        ...(password.trim() === '' ? {} : { smtpPass: password }),
      });
      setPassword('');
      // Le Rapport garde la réponse du serveur en cache : elle vient de changer.
      resetClientConfig();
      settings.reload();
      return address ? 'Enregistré.' : 'Configuration effacée.';
    });
  }

  return (
    <section className="panel">
      <h2 className="panel__title">Envoi des factures par email</h2>
      <p className="hint">
        Le compte Gmail depuis lequel les factures partent. Sans lui, elles restent
        téléchargeables mais « Envoyer par email » n’est pas disponible dans le Rapport.
      </p>

      {settings.error && (
        <p className="error" role="alert">
          {settings.error}
        </p>
      )}

      {managedByEnv && (
        <p className="hint hint--warning">
          Le serveur impose déjà un compte d’envoi : ce qui est saisi ici n’est pas utilisé.
        </p>
      )}

      <form className="form" onSubmit={onSubmit}>
        <label className="field">
          <span className="field__label">Adresse Gmail</span>
          <input
            className="input"
            type="email"
            autoComplete="username"
            placeholder="cabinet@gmail.com"
            value={user}
            onChange={(event) => setUser(event.target.value)}
          />
          <span className="hint">Vider ce champ efface la configuration.</span>
        </label>

        <label className="field">
          <span className="field__label">Mot de passe d’application</span>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            placeholder={configured ? '•••••••••••••••• (configuré)' : 'abcd efgh ijkl mnop'}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <span className="hint">
            Depuis le compte Google : Sécurité → Validation en deux étapes → Mots de passe des
            applications. Google l’affiche en quatre groupes de quatre lettres ; les espaces n’ont
            pas d’importance. Laisser vide conserve celui déjà enregistré.
          </span>
        </label>

        <label className="field">
          <span className="field__label">Nom affiché</span>
          <input
            className="input"
            placeholder="T. Lapierre — Ergothérapeute"
            value={fromName}
            onChange={(event) => setFromName(event.target.value)}
          />
          <span className="hint">Ce que le patient voit comme expéditeur. Facultatif.</span>
        </label>

        <label className="field">
          <span className="field__label">Objet de l’email</span>
          <input
            className="input"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">Texte de l’email</span>
          <textarea
            className="input input--textarea"
            rows={7}
            value={body}
            onChange={(event) => setBody(event.target.value)}
          />
          <span className="hint">
            Champs disponibles : {'{numero}'}, {'{date}'}, {'{mois}'}, {'{patient}'}, {'{prenom}'},{' '}
            {'{praticienne}'}. Rien de médical ici : la facture est en pièce jointe. Vider un champ
            remet le texte par défaut.
          </span>
        </label>

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}

        <div className="form__actions">
          <button className="button button--primary" type="submit" disabled={pending}>
            {pending ? 'Enregistrement…' : 'Enregistrer'}
          </button>

          <button
            type="button"
            className="button"
            disabled={pending || (!configured && !managedByEnv)}
            onClick={() =>
              void run(async () => {
                const { to } = await api.smtp.test();
                return `Email de test envoyé à ${to}.`;
              })
            }
          >
            Envoyer un email de test
          </button>

          {notice && <span className="muted">{notice}</span>}
        </div>
      </form>
    </section>
  );
}
